<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import {
  BaseDropdownActionList,
  BaseIconButton
} from '@cplug/design-system/components'
import {
  AvatarSize,
  BaseButtonSize,
  BaseButtonType,
  BaseIconName,
  MediaQuery
} from '@cplug/design-system/components/props'
import type { DropdownItem } from '@cplug/design-system/components/props'
import { useMediaQuery } from '@vueuse/core'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { useWhitelabelStore } from '@cplug/frontend-shared-domains/domain/whitelabel'
import { useTranslation } from './BaseHeaderRightSide.i18n'
import SidebarUserMenu from './SidebarUserMenu.vue'
import { ThemeOption, useThemePreferenceStore } from '~/domain/dashboard/settings/erp/shared'
import { useBrandStore } from '~/domain/shared/branding/stores/useBrandStore'
import sparklesIcon from '~/atoms/images/cplug-sparkles-glow-icon.svg'
import { ASSISTANT_INTEREST_PATH } from '~/domain/shared/assistant/models/AssistantInterest'
import { useAssistantSidebar } from '~/domain/shared/assistant/composables/useAssistantSidebar'
import useSupportStore from '~/domain/shared/support/stores/useSupportStore'
import useCompanyStore from '~/domain/dashboard/company/stores/useCompanyStore'
import type { Company } from '~/domain/shared/company/models/Company'
import useLoggedUserStore from '~/domain/dashboard/user/logged-user/stores/useLoggedUserStore'

const isNotificationDropdownOpen = ref(false)
const { t } = useTranslation()

const authStore = useAuthenticationStore()
const { companies: selectedCompanies } = storeToRefs(authStore)

const router = useRouter()
const isDesktop = useMediaQuery(MediaQuery.Desktop)

const companyStore = useCompanyStore()
const whitelabelStore = useWhitelabelStore()
const { isPartner, isWhitelabel } = storeToRefs(whitelabelStore)
const brandStore = useBrandStore()
const { isPartnerBrand, isTray } = storeToRefs(brandStore)

const supportStore = useSupportStore()
const { trayWhitelabelSupportUrl } = storeToRefs(supportStore)
const {
  isAssistantEnabled,
  isAssistantOpen,
  showAssistantComingSoon,
  toggleAssistantSidebar
} = useAssistantSidebar()

const showAssistantButton = computed(() => isAssistantEnabled.value || showAssistantComingSoon.value)
const assistantLabel = computed(() => showAssistantComingSoon.value ? t('AssistantComingSoon') : t('Assistant'))

const hasTraySupport = computed(() => isTray.value && !!trayWhitelabelSupportUrl.value)

const hasHelpButton = computed(() => {
  if (isTray.value) {
    return hasTraySupport.value
  }
  if (isPartnerBrand.value || isPartner.value || isWhitelabel.value) {
    return false
  }
  return true
})

companyStore.getPage(1, 100)
const selectedCompany = ref<Company | undefined>(selectedCompanies.value[0])
const loggedUserStore = useLoggedUserStore()

const themePreferenceStore = useThemePreferenceStore()
const { isDark } = storeToRefs(themePreferenceStore)

const iconButtonSize = computed(() => isDesktop.value ? BaseButtonSize.Normal : BaseButtonSize.Small)
const assistantIconClass = computed(() => isDesktop.value ? 'size-6' : 'size-4')
const assistantButtonClass = computed(() => isDesktop.value ? 'size-9' : 'size-8')
const mobileAvatarSize = computed(() => AvatarSize.Small)
const themeToggleIcon = computed(() => isDark.value ? BaseIconName.Sun : BaseIconName.Moon)
const themeToggleLabel = computed(() => isDark.value ? t('SwitchToLightMode') : t('SwitchToDarkMode'))

const helpMenu = ref<InstanceType<typeof BaseDropdownActionList>>()

function redirectHelpCenter (): void {
  router.push({ path: '/dashboard/help-center' })
}

function redirectSupport (): void {
  if (isTray.value && trayWhitelabelSupportUrl.value) {
    window.open(trayWhitelabelSupportUrl.value, '_blank', 'noopener,noreferrer')
    return
  }
  router.push({ path: '/dashboard/support' })
}

const helpMenuItems = computed<DropdownItem<() => void>[]>(() => [
  {
    key: 'help-center',
    label: t('HelpCenter'),
    leadIcon: BaseIconName.Help,
    value: redirectHelpCenter
  },
  {
    key: 'support',
    label: t('Support'),
    leadIcon: BaseIconName.Headphones,
    value: redirectSupport
  }
])

function onHelpMenuClick (item: DropdownItem<unknown>): void {
  if (typeof item.value !== 'function') {
    return
  }

  (item.value as () => void)()
}

function onClickHelp (event: MouseEvent): void {
  if (hasTraySupport.value) {
    redirectSupport()
    return
  }
  helpMenu.value?.toggle(event)
}

function onAssistantClick (): void {
  if (isAssistantEnabled.value) {
    toggleAssistantSidebar()
    return
  }

  const { href } = router.resolve(ASSISTANT_INTEREST_PATH)
  window.open(href, '_blank', 'noopener,noreferrer')
}

function onClickNotifications (): void {
  isNotificationDropdownOpen.value = !isNotificationDropdownOpen.value
}

function toggleTheme (): void {
  themePreferenceStore.setTheme(isDark.value ? ThemeOption.Light : ThemeOption.Dark)
}

watch(
  () => selectedCompany.value,
  () => {
    authStore.setCompanies(selectedCompany.value != null ? [selectedCompany.value] : [])
    loggedUserStore.clearPermissions()
    window.location.reload()
  }
)
</script>

<template>
  <div class="flex shrink-0 items-center justify-end">
    <div class="flex items-center gap-0 lg:gap-3">
      <BaseIconButton
        v-if="hasHelpButton"
        :contained="false"
        data-testid="btn-icon-help"
        :icon="hasTraySupport ? BaseIconName.Headphones : BaseIconName.Help"
        :size="iconButtonSize"
        :type="BaseButtonType.Secondary"
        @click="onClickHelp"
      />

      <BaseDropdownActionList
        v-if="hasHelpButton && !hasTraySupport"
        ref="helpMenu"
        :options="helpMenuItems"
        @click="onHelpMenuClick"
      />

      <BaseIconButton
        :contained="false"
        data-testid="btn-icon-notification"
        :icon="BaseIconName.Bell"
        :size="iconButtonSize"
        :type="BaseButtonType.Secondary"
        @click="onClickNotifications"
      />

      <button
        v-if="showAssistantButton"
        type="button"
        data-testid="btn-icon-assistant"
        :aria-label="assistantLabel"
        :aria-pressed="isAssistantOpen"
        class="relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 hover:bg-muted"
        :class="assistantButtonClass"
        @click="onAssistantClick"
      >
        <img
          :src="sparklesIcon"
          alt=""
          class="pointer-events-none"
          :class="assistantIconClass"
        >
        <span
          v-if="showAssistantComingSoon"
          aria-hidden="true"
          data-testid="assistant-soon-badge"
          class="bg-warning-strong text-primary-foreground ring-sidebar absolute -top-1 -right-1 z-10 rounded-full px-1 py-px text-[8px] leading-none font-bold whitespace-nowrap ring-2"
        >
          {{ t('SoonBadge') }}
        </span>
      </button>

      <BaseIconButton
        :contained="false"
        data-testid="btn-icon-theme-toggle"
        :icon="themeToggleIcon"
        :size="iconButtonSize"
        :type="BaseButtonType.Secondary"
        :aria-label="themeToggleLabel"
        @click="toggleTheme"
      />

      <SidebarUserMenu
        v-if="!isDesktop"
        :avatar-size="mobileAvatarSize"
      />
    </div>
  </div>
</template>
